/**
 * Reading the RFC 7807 documents this API answers with
 * (docs/first_release/12-api-rest.md §1.2).
 *
 * Every error has the same shape, so every screen can handle errors the same
 * way: a 422 becomes messages under the inputs that caused it (`13 §6`), a 500
 * shows its `request_id` so a user can quote it, and anything else becomes one
 * sentence in a banner.
 */
export interface ApiProblemViolation {
  /** A dotted path into the request body, e.g. `communication.default_effect`. */
  field: string;
  /** What code branches on. Never translated. */
  code: string;
  /** What a person reads. */
  message: string;
}

export interface ApiProblem {
  type: string;
  title: string;
  status: number;
  detail?: string | null;
  instance?: string | null;
  request_id: string;
  errors?: ApiProblemViolation[] | null;
  /**
   * RFC 7807 §3.2 extension members. The API uses them where a client needs a
   * value and not prose: `warnings` on a failed publish or preview, so the two
   * endpoints never disagree about the same draft, and
   * `unchanged_since_version` on the 409 that refuses an unchanged publish, so
   * the version screen can name the version it matched.
   */
  [extension: string]: unknown;
}

export function isApiProblem(value: unknown): value is ApiProblem {
  return (
    typeof value === 'object' &&
    value !== null &&
    'title' in value &&
    'status' in value &&
    typeof (value as ApiProblem).status === 'number'
  );
}

/**
 * The one sentence to show a user.
 *
 * `detail` is specific to this occurrence and `title` is generic, so the
 * detail wins when there is one. Both llegan ya en el idioma del usuario: el
 * backend traduce el catálogo de errores contra `users.locale` y, sin sesión,
 * contra `Accept-Language`.
 *
 * El `request_id` del 500 ya NO se concatena aquí. Es lo único que un usuario
 * puede decirle a soporte (`13 §6`) y por eso el banner lo pinta como una
 * pastilla que se copia al pulsarla; pegado dentro de la frase no se podía
 * seleccionar sin arrastrar media línea.
 */
export function problemMessage(problem: ApiProblem): string {
  return problem.detail?.trim() || problem.title;
}

/**
 * The `errors[]` of a 422, keyed by field, ready to hand to react-hook-form.
 *
 * Only the first message per field survives: an input can show one message, and
 * showing the first is more useful than concatenating three.
 */
export function fieldErrors(problem: ApiProblem): Record<string, string> {
  const byField: Record<string, string> = {};

  for (const violation of problem.errors ?? []) {
    byField[violation.field] ??= violation.message;
  }

  return byField;
}

/**
 * Thrown by `authFetch` callers that want one `catch` for every failure.
 */
export class ApiError extends Error {
  constructor(readonly problem: ApiProblem) {
    super(problemMessage(problem));
    this.name = 'ApiError';
  }

  get status(): number {
    return this.problem.status;
  }

  get fields(): Record<string, string> {
    return fieldErrors(this.problem);
  }
}

/**
 * A network failure that never reached the server. Distinct from `ApiError`
 * because the screens treat them differently: this one gets a retry button,
 * a 422 does not.
 */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    // Este texto no llega a ninguna pantalla: `ErrorBanner` traduce la clase
    // del error, no su `message`. Se queda en inglés a propósito, como el resto
    // de mensajes que solo ve quien lee una traza.
    super('The server could not be reached.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}
